import React, { useState, useEffect } from 'react';
import API from 'config/ApiConfig';
import { Mosaic } from 'react-loading-indicators';

// constants
const API_BASE_URL = API.API_ADMIN;
function getToken() {
    return localStorage.getItem('authToken');
  }

// utility function
function getAuthHeaders() {
    const token = getToken();
    if (!token) {
        throw new Error('No token available');
    }
    return {
        'Authorization': `Bearer ${token}`, // Format token JWT
        'Content-Type': 'application/json' // Jika perlu, untuk menentukan jenis konten
    };
  }
// Utility function to validate if a value is a valid number
const toValidNumber = (value) => {
    const number = parseFloat(value);
    return isNaN(number) ? 0 : number;
};

export default function ViewPendapatan() {
    const [presensi, setPresensi] = useState([]);
    const [siswaData, setSiswaData] = useState({});
    const [tentorsData, setTentorsData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortMonth, setSortMonth] = useState('');

    useEffect(() => {
        const fetchPresensi = async () => {
            try {
                // Fetch all presensi data
                const response = await fetch(`${API_BASE_URL}/rekap`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                setPresensi(data);
            } catch (error) {
                setError(error.message);
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchSiswaData = async () => {
            try {
                // Fetch all siswa data
                const response = await fetch(`${API_BASE_URL}/siswa`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                // Map siswa data by ID for quick lookup
                const siswaMap = data.reduce((acc, siswa) => {
                    if (siswa && siswa._id) {
                        acc[siswa._id] = siswa;
                    }
                    return acc;
                }, {});
                setSiswaData(siswaMap);
            } catch (error) {
                setError(error.message);
                console.error('Error fetching siswa data:', error);
            }
        };

        const fetchTentorsData = async () => {
            try {
                // Fetch all tentor data
                const response = await fetch(`${API_BASE_URL}/tentor`, {
                    method: 'GET',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                // Map tentor data by ID for quick lookup
                const tentorsMap = data.reduce((acc, tentor) => {
                    if (tentor && tentor._id) {
                        acc[tentor._id] = tentor;
                    }
                    return acc;
                }, {});
                setTentorsData(tentorsMap);
            } catch (error) {
                setError(error.message);
                console.error('Error fetching tentors data:', error);
            }
        };

        fetchPresensi();
        fetchSiswaData();
        fetchTentorsData();
    }, []);

    // Filter and sort presensi data by month
    const filteredPresensi = presensi.filter(p => {
        const month = new Date(p.waktu_pembelajaran).getMonth() + 1;
        return !sortMonth || month === parseInt(sortMonth, 10);
    }).sort((a, b) => new Date(a.waktu_pembelajaran) - new Date(b.waktu_pembelajaran));

    // Calculate total pendapatan kotor and pendapatan bersih
    const totalPendapatanKotor = filteredPresensi.reduce((total, p) => total + toValidNumber(p.harga), 0);
    const totalPendapatanBersih = filteredPresensi.reduce((total, p) => total + toValidNumber(p.pendapatan), 0);

    const monthNames = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
    ];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR'
        }).format(value);
    };

    if (loading) {
        return <div className="flex m-4 justify-center items-center w-screen"><Mosaic color="#1177bc" size="large" text="Loading.." textColor="" /></div>
      }
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="container mx-auto p-4">
            <div className="flex flex-wrap mb-4">
                <label className="mr-4 mt-2">
                    Sort by Month:
                    <select
                        className="px-2 py-1 rounded-lg shadow ml-2"
                        value={sortMonth}
                        onChange={(e) => setSortMonth(e.target.value)}
                    >
                        <option value="" className="px-4">All</option>
                        {monthNames.map((month, index) => (
                            <option key={index} value={index + 1}>
                                {month}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className='flex flex-row mt-8'>
                <div className="mr-2">
                    <div className="bg-white p-4 rounded shadow border border-blueGray-300 mb-2">
                        <p className="font-semibold text-blueGray-600 mb-4">Pendapatan Kotor</p>
                        <p className="text-blueGray-600"><b>{formatCurrency(totalPendapatanKotor)}</b></p>
                    </div>
                </div>
                <div className="space-y-4 mb-2">
                    <div className="bg-white p-4 rounded shadow border border-blueGray-300 mb-2">
                        <p className="font-semibold text-blueGray-600 mb-4">Pendapatan Bersih</p>
                        <p className="text-blueGray-600"><b>{formatCurrency(totalPendapatanBersih)}</b></p>
                    </div>
                </div>
            </div>
            <div className="block w-full overflow-x-auto">
            <table className="w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                <thead>
                    <tr>
                        <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-medium text-gray-600">Tanggal</th>
                        <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-medium text-gray-600">Siswa</th>
                        <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-medium text-gray-600">Tentor</th>
                        <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-medium text-gray-600">Pendapatan Kotor</th>
                        <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-medium text-gray-600">Pendapatan Bersih</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredPresensi.map((item) => {
                        const siswa = siswaData[item.siswa_id] || {};
                        const tentor = tentorsData[item.tentor_id] || {};

                        return (
                            <tr key={item._id}>
                                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                    {new Date(item.waktu_pembelajaran).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                    {siswa.nama || 'Unknown'}
                                </td>
                                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                    {tentor.nama || 'Unknown'}
                                </td>
                                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                    {formatCurrency(toValidNumber(item.harga))}
                                </td>
                                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                    {formatCurrency(toValidNumber(item.pendapatan))}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
        </div>
    );
}
